import { motion } from 'framer-motion';
import { Link, useParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';

import { PreviousThreads } from '../../../components/previous-threads/previous-threads';
import { useGetThreadExistsInNewKim } from '@/api/queries/useGetThreadExistsInNewKim';
import { useUserContext } from '@/Contexts/User';
import { Icon } from '@/new/components/icon';
import { NewChip } from '@/new/components/new-chip';
import { Button } from '@/new/components/shadcn/button';
import { Skeleton } from '@/new/components/shadcn/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { ThreadMessage } from '@/types';

interface AnswerSidebarProps {
  isLoadingMessages: boolean;
  userMessage: ThreadMessage | undefined;
}

const ChatBubble = ({ text, isLoadingMessages }: { text: string; isLoadingMessages: boolean }) => {
  return (
    <div className="m-3 max-h-52 w-fit max-w-[90%] overflow-y-auto rounded-xl rounded-bl-none border bg-gray-50 px-3 py-2 text-xs whitespace-pre-wrap">
      {isLoadingMessages ? <Skeleton className="h-4 w-24" /> : text}
    </div>
  );
};

export function AnswerSidebar({ userMessage, isLoadingMessages }: AnswerSidebarProps) {
  const { matterId, subsetId, threadId } = useParams();
  const navigate = useNavigate();
  const { user } = useUserContext();
  const { data: existsInNewKim } = useGetThreadExistsInNewKim(matterId!, subsetId!, threadId!);

  const newKimHref = existsInNewKim
    ? `/matters/${matterId}/subsets/${subsetId}/new-kim/${threadId}`
    : `/matters/${matterId}/subsets/${subsetId}/new-kim?question=${encodeURIComponent(userMessage?.content ?? '')}`;

  return (
    <div className="flex h-full w-56 min-w-56 flex-col border-r">
      <motion.div initial={{ height: '50%' }} animate={{ height: '50%' }} className="flex flex-col border-b">
        <div className="flex h-14 items-center justify-between border-b bg-gray-50/50 px-4 py-3">
          <div className="flex items-center gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button asChild size="iconSm" className="rounded-full p-1">
                  <Link to={`/matters/${matterId}/subsets/${subsetId}/kim`}>
                    <Icon className="lucide--arrow-left" />
                  </Link>
                </Button>
              </TooltipTrigger>
              <TooltipContent>Back to all questions</TooltipContent>
            </Tooltip>

            <h3 className="text-sm font-medium text-gray-800">Question</h3>
          </div>

          <Button
            onClick={() => {
              navigate(`/matters/${matterId}/subsets/${subsetId}/kim`);
            }}
            size="sm"
            variant="primary"
            disabled={isLoadingMessages}
          >
            <Icon className="lucide--plus" />
            New
          </Button>
        </div>

        <div className="flex flex-1 flex-col items-start justify-between gap-2 overflow-y-hidden">
          <ChatBubble text={userMessage ? userMessage.content : '...'} isLoadingMessages={isLoadingMessages} />

          {/* Link to thread in new kim */}
          {user?.id === userMessage?.createdById && (
            <div className="relative space-y-1.25 border-t p-3 px-3.5 pt-3.75 text-xs">
              <NewChip className="-top-2 right-auto mb-2 -ml-px block w-fit py-0.25" />
              <p className="leading-tight font-medium">Want to ask follow-up questions?</p>
              <p className="text-gray-500">
                See how the same question is answered in the{' '}
                <a href={newKimHref} target="_blank" className="group text-blue-500 duration-150 hover:text-blue-600">
                  improved Kim experience{' '}
                  <Icon className="lucide--arrow-up-right -mb-0.5 -ml-0.5 inline-block duration-150 group-hover:-mb-px group-hover:ml-0" />
                </a>
              </p>
              <p className="text-gray-500">Opens in a new tab.</p>
            </div>
          )}
        </div>
      </motion.div>

      <motion.div initial={{ height: '50%' }} animate={{ height: '50%' }} className="flex flex-col">
        <div className="h-12 border-b bg-gray-50/50 px-4 py-3">
          <h3 className="text-sm font-medium text-gray-800">Question History</h3>
        </div>
        <div className="flex-1 overflow-auto">
          <PreviousThreads />
        </div>
      </motion.div>
    </div>
  );
}
